import { useState, useEffect, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.min.mjs";
import mammoth from "mammoth";
// TinyMCE
import { Editor } from "@tinymce/tinymce-react";
import { Button, Modal } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import DefaultButton from "../../../../shared/components/DefaultButton";
import {loadCategoryArticleAddDataRequest,loadCategoryArticleDetailDataRequest,loadCategoryArticleUpdateDataRequest,setCategoryArticleAddDataReset,setCategoryArticleUpdateDataReset,loadCategoryArticleAddS3DataRequest} from "../redux/Actions";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../../shared/components/Loader";
import { addErrorAlert } from "../../../alert/Actions";

const CategoryKB = ({ show, onHide, initialData, onSuccess }) => {

  const dispatch = useDispatch();
  const isEditMode = Boolean(initialData?.category?.article_id);
  const { categoryarticleDetail, articles, loading: listLoading , loading: articleLoading, updateSuccess, addSuccess, s3UploadLoading, s3UploadSuccess, s3UploadUrl, } = useSelector( (state) => state.articleList);
  const [editorContent, setEditorContent] = useState("");
  const [documentLinks, setDocumentLinks] = useState([""]);
  const [fieldError, setFieldError] = useState("");
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const s3FileInputRef = useRef(null);  
  console.log("article", categoryarticleDetail);

  useEffect(() => {
    if (isEditMode && categoryarticleDetail) {
      const links = categoryarticleDetail.external_link;

      if (!links) {
        setDocumentLinks([""]);                      // nothing → empty input
      } else if (Array.isArray(links)) {
        setDocumentLinks(links);                     // array → keep as is
      } else {
        setDocumentLinks([links]);                   // single string → wrap into array
      }
    }
  }, [categoryarticleDetail, isEditMode]);



  useEffect(() => {
    if (editorRef.current && isEditMode && categoryarticleDetail) {
      editorRef.current.setContent(categoryarticleDetail.content || "");
    }
  }, [categoryarticleDetail, isEditMode]);


  const addDocumentLink = () => {
    setDocumentLinks([...documentLinks, ""]);
  };

  const removeDocumentLink = (index) => {
    setDocumentLinks((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length === 0 ? [] : updated;
    });
  };


  const updateDocumentLink = (index, value) => {
    const updated = [...documentLinks];
    updated[index] = value;
    setDocumentLinks(updated);
  };



  /* -----------------------------------------
     LOAD INITIAL DATA FOR EDIT MODE
  ------------------------------------------ */
  useEffect(() => {
    if (isEditMode && initialData?.category?.article_id) {
      dispatch(
        loadCategoryArticleDetailDataRequest(initialData?.category?.article_id)
      );
    }
  }, [dispatch, isEditMode, initialData?.category?.article_id]);

  useEffect(() => {
    if (isEditMode && categoryarticleDetail) {
      setEditorContent(categoryarticleDetail.content || "");
    }
  }, [categoryarticleDetail, isEditMode]);

  useEffect(() => {
    if (show && !isEditMode) {
      setDocumentLinks([""]); // RESET URL INPUTS FOR ADD MODE
      setEditorContent("");    // clear editor too
    }
  }, [show, isEditMode]);

  useEffect(() => {
    if (!show) {
      setFieldError("");   // CLEAR ERROR WHEN MODAL CLOSES
    }
  }, [show]);
  
  /* -----------------------------------------
     FILE HANDLING
  ------------------------------------------ */
  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "txt") readTxt(file);
    else if (ext === "docx" || ext === "doc") readDocx(file);
    else if (ext === "pdf") readPdf(file);
    else alert("Only PDF, DOCX, and TXT are supported.");
  };

  const readTxt = (file) => {
    const reader = new FileReader();
    reader.onload = () => insertIntoEditor(reader.result);
    reader.readAsText(file);
  };

  const readDocx = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });
    insertIntoEditor(result.value);
  };

  const readPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let text = "";

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((i) => i.str).join(" ") + "\n\n";
    }

    insertIntoEditor(text);
  };

  // Insert into TinyMCE (NOT CKEditor)
  const insertIntoEditor = (content) => {
    if (editorRef.current) {
      editorRef.current.setContent(content);
      setEditorContent(content);
    }
  };

  /* -----------------------------------------
              S3 upload
  ------------------------------------------ */

  const handleS3UploadClick = () => {
    s3FileInputRef.current?.click();
  };

  const handleS3FileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif"
    ];

    if (!allowedTypes.includes(file.type)) {
      dispatch(addErrorAlert("Only PDF, Excel, CSV, and image files (JPEG, PNG, GIF) are allowed"));
      return;
    }

    dispatch(
      loadCategoryArticleAddS3DataRequest({
        file,
        category_config_id: initialData?.category?.id,
        title: editorRef.current?.getContent({ format: "text" }) || "File Upload",
        content: editorContent || "File Upload",
      })
    );

    e.target.value = "";
  };


   useEffect(() => {
      if (s3UploadUrl?.fileUrl) {
        setDocumentLinks((prev) => {
          // If in edit mode with existing links, keep them and add the new S3 URL
          const links = prev.filter((l) => l && l.trim() !== ""); // keep non-empty existing links
          if (!links.includes(s3UploadUrl.fileUrl)) {
            links.push(s3UploadUrl.fileUrl); // append new S3 URL
          }
          return links;
        });
      }
    }, [s3UploadUrl]);

  /* -----------------------------------------
     SAVE HANDLER
  ------------------------------------------ */
  const handleSave = (formValues, { setSubmitting }) => {
    const cleanedLinks = documentLinks.filter((v) => v.trim() !== "");

    // const docLinkRegex = /^https?:\/\/.+\.(pdf|txt|png|jpg|jpeg|mp4)$/i;

    // const invalidLink = cleanedLinks.find((url) => !docLinkRegex.test(url));

    // if (invalidLink) {
    //   setFieldError(
    //     "Document link must start with http/https and end with .pdf, .png, .jpg, .jpeg, .mp4, or .txt."
    //   );
    //   setSubmitting(false);
    //   return; // stop form submit
    // }

    // clear error when valid
    setFieldError("");
    const payload = {
      category_config_id: initialData?.category?.id,
      title: formValues.articleName,
      content: formValues.articleContent,
      external_link: cleanedLinks.length === 0 ? null : cleanedLinks,
      //s3: isS3Link
    };
    

    if (isEditMode) {
      dispatch(
        loadCategoryArticleUpdateDataRequest(
          initialData?.category?.article_id,
          payload
        )
      );
    } else {
      dispatch(loadCategoryArticleAddDataRequest(payload));
    }

    setSubmitting(false);
  };

  useEffect(() => {
    if (updateSuccess) {
      onHide();
      onSuccess();     // notify parent to reload
      dispatch(setCategoryArticleUpdateDataReset());
    }
    if (addSuccess) {
      onHide();
      onSuccess();     // notify parent to reload
      dispatch(setCategoryArticleAddDataReset());
    }
  }, [updateSuccess, addSuccess, onSuccess]);


  /* -----------------------------------------
     VALIDATION
  ------------------------------------------ */
  const Schema = Yup.object().shape({
    articleName: Yup.string().required("Article Title is required"),
    // documentLink: Yup.string().url("Enter a valid URL").nullable(),
    articleContent: Yup.string().test(
      "not-empty",
      "Content cannot be empty",
      (value) => value && value.replace(/<[^>]+>/g, "").trim().length > 0
    )
  });

  

  /* -----------------------------------------
     RENDER
  ------------------------------------------ */
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode ? "Edit Article" : "Create Article"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {fieldError && (
          <div className="alert alert-danger" role="alert">
            {fieldError}
          </div>
        )}
        {isEditMode && articleLoading ? (
          <>
            <div
              style={{ textAlign: "center", padding: 30 }}
            >
              <Loader />
            </div>

          </>
        ) : (
          <Formik
            initialValues={{
              articleName: isEditMode ? categoryarticleDetail?.title ?? "" : "",
              documentLink: isEditMode
                ? categoryarticleDetail?.external_link ?? ""
                : "",
              articleContent: editorContent
            }}
            validationSchema={Schema}
            onSubmit={handleSave}
          // enableReinitialize={true}
          >
            {({
              handleSubmit,
              setFieldValue,
              errors,
              touched,    
              isSubmitting,
              isValid
            }) => (
              <Form onSubmit={handleSubmit}>
                {/* --------------------------------------- */}
                {/* ARTICLE NAME */}
                {/* --------------------------------------- */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 600 }}>Article Name</label>
                  <Field
                    name="articleName"
                    placeholder="Enter article name"
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #ccc"
                    }}
                  />
                  <div style={{ color: "red" }}>
                    <ErrorMessage name="articleName" />
                  </div>
                </div>

                {/* --------------------------------------- */}
                {/* DOCUMENT LINK */}
                {/* --------------------------------------- */}
                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 600 }}>Document Link</label>

                    {documentLinks.length === 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <button
                          type="button"
                          onClick={addDocumentLink}
                          style={{
                            height: "38px",
                            padding: "0 12px",
                            borderRadius: "6px",
                            border: "none",
                            background: "#28a745",
                            color: "#fff",
                            cursor: "pointer"
                          }}
                        >
                          + Add document link
                        </button>
                      </div>
                    )}

                    {documentLinks.map((link, index) => {
                      const isLast = index === documentLinks.length - 1;

                      return (
                        <div
                          key={index}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 40px 40px",
                            gap: "8px",
                            marginBottom: "10px",
                            alignItems: "center"
                          }}
                        >
                          {/* INPUT */}
                          <input
                            type="text"
                            value={link}
                            onChange={(e) =>
                              updateDocumentLink(index, e.target.value)
                            }
                            placeholder="https://example.com/doc.pdf"
                            style={{
                              width: "100%",
                              padding: "8px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              height: "38px"
                            }}
                          />

                          {/* REMOVE (ALWAYS ENABLED) */}
                          <button
                            type="button"
                            onClick={() => removeDocumentLink(index)}
                            style={{
                              height: "38px",
                              width: "38px",
                              borderRadius: "6px",
                              border: "none",
                              background: "#dc3545",
                              color: "#fff",
                              fontSize: "20px",
                              lineHeight: "1",
                              cursor: "pointer"
                            }}
                            title="Remove"
                          >
                            –
                          </button>

                          {/* ADD (ONLY LAST ROW) */}
                          {isLast ? (
                            <button
                              type="button"
                              onClick={addDocumentLink}
                              style={{
                                height: "38px",
                                width: "38px",
                                borderRadius: "6px",
                                border: "none",
                                background: "#28a745",
                                color: "#fff",
                                fontSize: "20px",
                                lineHeight: "1",
                                cursor: "pointer"
                              }}
                              title="Add"
                            >
                              +
                            </button>
                          ) : (
                            <div />
                          )} 
                        </div>
                      );
                    })}
                  </div>
                {/* --------------------------------------- */}
                {/* TINYMCE EDITOR */}
                {/* --------------------------------------- */}
                <div style={{ marginBottom: 40 }}>
                  <Editor
                    tinymceScriptSrc='/tinymce/tinymce.min.js'
                    licenseKey='gpl'
                    onInit={(evt, editor) => {
                      editorRef.current = editor;
                      editor.setContent(editorContent || "");
                    }}
                    value={editorContent}
                    onEditorChange={(content) => {
                      setEditorContent(content);
                      setFieldValue("articleContent", content);
                    }}
                    init={{
                      height: 500,
                      menubar: false,
                      statusbar: false,
                      toolbar_mode: "sliding",
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                      ],
                      toolbar:
                        "undo redo | blocks | bold italic underline forecolor | " +
                        "alignleft aligncenter alignright alignjustify | " +
                        "bullist numlist outdent indent | removeformat | help",
                      //  FIX DOUBLE SPACING 
                      content_style: "p { margin: 0 !important; }"
                    }}

                  />

                  {errors.articleContent && touched.articleContent && (
                    <div style={{ color: "red", marginTop: 5 }}>
                      {errors.articleContent}
                    </div>
                  )}
                </div>

                {/* HIDDEN FILE INPUT */}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf,.txt,.doc,.docx"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />
                <input
                  type="file"
                  ref={s3FileInputRef}
                  accept=".pdf,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif"
                  onChange={handleS3FileUpload}
                  style={{ display: "none" }}
                />


                {/* --------------------------------------- */}
                {/* BUTTONS */}
                {/* --------------------------------------- */}
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleUploadClick}

                  >
                    Upload File
                  </Button>
                  <Button
                    type="button"
                    variant="success"
                    onClick={handleS3UploadClick}
                    disabled={s3UploadLoading}
                  >
                    {s3UploadLoading ? "Uploading..." : "Upload to S3"}
                  </Button>
                  <DefaultButton
                    buttonTitle="Save Article"
                    buttonType="submit"
                    disabled={isSubmitting || !isValid}
                    buttonClass="btn btn-secondary default-btn-color"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CategoryKB;
